import { MapBounds, OfflineArea, DownloadProgress } from '../types';
import {
  TILE_SIZE_ESTIMATES,
  MAP_LAYERS,
} from '../constants';
import { putTile, deleteTile, hasTile } from './tileCache';

type OfflineLayer = 'NORWAY_TOPO' | 'SATELLITE';

/**
 * De faktiske flis-malene hvert offline-«lag» rendrer på kartet. Tidligere lastet
 * vi kun Kartverket for topo, mens kartet også viser OpenTopoMap → nedlastede
 * fliser matchet ikke det som ble vist offline. Nå laster vi ned nøyaktig det
 * <BaseTileLayer> ber om, så offline-opplevelsen er identisk med online.
 */
export const OFFLINE_LAYER_TEMPLATES: Record<OfflineLayer, string[]> = {
  // Terreng: Kartverket (default Norge-visning) + OpenTopoMap (terreng-toggle på).
  NORWAY_TOPO: [MAP_LAYERS.NORWAY_TOPO, MAP_LAYERS.TOPO],
  // Flyfoto: satellitt + stedsnavn-labels (rendres alltid sammen).
  SATELLITE: [MAP_LAYERS.SATELLITE, MAP_LAYERS.SATELLITE_LABELS],
};

/** Bygger en konkret XYZ-URL fra en Leaflet-mal ({s}/{z}/{x}/{y}/{r}). */
export function buildXyzUrl(template: string, z: number, x: number, y: number): string {
  return template
    .replace(/\{s\}/g, 'a')
    .replace(/\{z\}/g, String(z))
    .replace(/\{x\}/g, String(x))
    .replace(/\{y\}/g, String(y))
    .replace(/\{r\}/g, '');
}

// --- Tile-matematikk (Web Mercator / WMTS standard) ---

export function latLngToTile(lat: number, lng: number, zoom: number): { x: number; y: number } {
  const n = Math.pow(2, zoom);
  const x = Math.floor(((lng + 180) / 360) * n);
  const latRad = (lat * Math.PI) / 180;
  const y = Math.floor(
    ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n
  );
  return { x, y };
}

export function getTilesForBounds(
  bounds: MapBounds,
  zoom: number
): Array<{ z: number; x: number; y: number }> {
  const nw = latLngToTile(bounds.latMax, bounds.lonMin, zoom);
  const se = latLngToTile(bounds.latMin, bounds.lonMax, zoom);
  const tiles: Array<{ z: number; x: number; y: number }> = [];
  for (let x = nw.x; x <= se.x; x++) {
    for (let y = nw.y; y <= se.y; y++) {
      tiles.push({ z: zoom, x, y });
    }
  }
  return tiles;
}

/**
 * Bygger primær tile-URL for et lag (brukes til forhåndsvisning i UI-et).
 * NB: Kartverket/ArcGIS bruker {z}/{y}/{x} — buildXyzUrl navnesubstituerer, så
 * rekkefølgen i malen bevares korrekt.
 */
export function buildTileUrl(
  layer: OfflineLayer,
  z: number,
  x: number,
  y: number
): string {
  return buildXyzUrl(OFFLINE_LAYER_TEMPLATES[layer][0], z, x, y);
}

/** Alle konkrete flis-URL-er et område dekker, på tvers av lag og maler. */
export function buildAreaUrls(area: OfflineArea): string[] {
  const urls: string[] = [];
  for (const layer of area.layers) {
    for (const template of OFFLINE_LAYER_TEMPLATES[layer]) {
      for (let z = area.zoomMin; z <= area.zoomMax; z++) {
        for (const { z: tz, x, y } of getTilesForBounds(area.bounds, z)) {
          urls.push(buildXyzUrl(template, tz, x, y));
        }
      }
    }
  }
  return urls;
}

// --- Estimering ---

export function estimateTileCount(
  bounds: MapBounds,
  zoomMin: number,
  zoomMax: number,
  layers: OfflineLayer[]
): { tileCount: number; estimatedBytes: number } {
  let tilesPerLayer = 0;
  for (let z = zoomMin; z <= zoomMax; z++) {
    tilesPerLayer += getTilesForBounds(bounds, z).length;
  }
  // Hvert lag laster ned flere maler (f.eks. satellitt + labels).
  let tileCount = 0;
  let estimatedBytes = 0;
  for (const layer of layers) {
    const templates = OFFLINE_LAYER_TEMPLATES[layer].length;
    tileCount += tilesPerLayer * templates;
    estimatedBytes += tilesPerLayer * templates * TILE_SIZE_ESTIMATES[layer];
  }
  return { tileCount, estimatedBytes: Math.round(estimatedBytes) };
}

// --- Nedlasting ---

const BATCH_SIZE = 6;
const BATCH_DELAY_MS = 50;

export async function downloadTiles(
  area: OfflineArea,
  onProgress: (progress: DownloadProgress) => void,
  signal: AbortSignal
): Promise<void> {
  const allUrls = buildAreaUrls(area);
  const total = allUrls.length;
  let downloaded = 0;
  let failed = 0;

  for (let i = 0; i < allUrls.length; i += BATCH_SIZE) {
    if (signal.aborted) throw new DOMException('Avbrutt', 'AbortError');

    const batch = allUrls.slice(i, i + BATCH_SIZE);
    await Promise.all(
      batch.map(async (url) => {
        try {
          // Hopp over allerede cachede fliser (gjenopptakelse/re-nedlasting).
          if (await hasTile(url)) {
            downloaded++;
            return;
          }
          const response = await fetch(url, { signal });
          if (response.ok) {
            // pin=true → bevisst lagret område, aldri auto-evictet av LRU.
            await putTile(url, response, true);
            downloaded++;
          } else {
            failed++;
          }
        } catch {
          failed++;
        }
      })
    );

    onProgress({ areaId: area.id, downloaded, total, failed });

    // Liten pause mellom batches for å ikke overbelaste tile-serverne
    if (i + BATCH_SIZE < allUrls.length) {
      await new Promise((r) => setTimeout(r, BATCH_DELAY_MS));
    }
  }
}

export async function deleteTilesForArea(area: OfflineArea): Promise<void> {
  for (const url of buildAreaUrls(area)) {
    await deleteTile(url);
  }
}

// --- Metadata (localStorage) ---

const OFFLINE_AREAS_KEY = 'fricamp_offline_areas';

export function getAllOfflineAreas(): OfflineArea[] {
  try {
    const raw = localStorage.getItem(OFFLINE_AREAS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function saveOfflineArea(area: OfflineArea): void {
  const areas = getAllOfflineAreas();
  const idx = areas.findIndex((a) => a.id === area.id);
  if (idx >= 0) {
    areas[idx] = area;
  } else {
    areas.push(area);
  }
  localStorage.setItem(OFFLINE_AREAS_KEY, JSON.stringify(areas));
}

export function deleteOfflineAreaMetadata(id: string): void {
  const areas = getAllOfflineAreas().filter((a) => a.id !== id);
  localStorage.setItem(OFFLINE_AREAS_KEY, JSON.stringify(areas));
}

export async function getTileStorageEstimateBytes(): Promise<number> {
  if ('storage' in navigator && 'estimate' in navigator.storage) {
    const estimate = await navigator.storage.estimate();
    return estimate.usage ?? 0;
  }
  return 0;
}
